import { useMemo, useState } from "react";
import axios from "axios";
import Spinner from "../../../ui/spinner/Spinner";
import Checkbox from "../../../ui/checkbox/Checkbox";
import styles from "./ChangeUid.module.css";
import {
  normalizePhone,
  isValidEmail,
  isValidPhone,
  isValidUsername,
} from "../../../../auth/userCredentialsUtils";

const UID_OPTIONS = {
  email: "email",
  phone: "phone",
  username: "username",
};

const ChangeUid = (props) => {
  const [showSpinner, setShowSpinner] = useState(false);

  const [step, setStep] = useState("password");
  const [selectedType, setSelectedType] = useState("");

  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [username, setUsername] = useState("");

  const [passwordError, setPasswordError] = useState("");
  const [uidError, setUidError] = useState("");
  const [serverError, setServerError] = useState("");

  const clearMessages = () => {
    setPasswordError("");
    setUidError("");
    setServerError("");
  };

  const resetUidFields = () => {
    setEmail("");
    setPhone("");
    setUsername("");
    setUidError("");
    setServerError("");
  };

  const selectedValue = useMemo(() => {
    if (selectedType === UID_OPTIONS.email) return email;
    if (selectedType === UID_OPTIONS.phone) return phone;
    if (selectedType === UID_OPTIONS.username) return username;
    return "";
  }, [selectedType, email, phone, username]);

  const validateSelectedUid = () => {
    setUidError("");
    setServerError("");

    if (selectedType === UID_OPTIONS.email) {
      if (!email) {
        setUidError("Please enter your new email.");
        return false;
      }
      if (!isValidEmail(email)) {
        setUidError("Your email is invalid.");
        return false;
      }
      return true;
    }

    if (selectedType === UID_OPTIONS.phone) {
      const cleanedPhone = normalizePhone(phone);

      if (!cleanedPhone) {
        setUidError("Please enter your new phone number.");
        return false;
      }
      if (!isValidPhone(cleanedPhone)) {
        setUidError("Your phone number is invalid.");
        return false;
      }
      return true;
    }

    if (selectedType === UID_OPTIONS.username) {
      if (!username) {
        setUidError("Please enter your new username.");
        return false;
      }
      if (!isValidUsername(username)) {
        setUidError("Your username is invalid.");
        return false;
      }
      return true;
    }

    setUidError("Please select the type of id you want to change.");
    return false;
  };

  const selected_uid = () => {
    if (selectedType === UID_OPTIONS.email) {
      return email.trim().toLowerCase()
    }

    if (selectedType === UID_OPTIONS.phone) {
      return email.trim().toLowerCase();
    }

    return username.trim().toLowerCase();
  };

  const verifyPasswordHandler = async () => {
    clearMessages();

    if (!password.trim()) {
      setPasswordError("Please enter your current password.");
      return;
    }

    try {
      setShowSpinner(true);

      const response = await axios.post(
        "php/sign_in/check_password.php",
        { password: password.trim() },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      //console.log("RAW RESPONSE", response);
      //console.log("DATA", response.data);
      const data = response.data;
      console.log("DATA", data);
      
      if (data.code !== 200) {
        alert(
          data.message ||
            "The server may not be available. Please check your internet connection",
        );
        setServerError(data?.message || "Verification failed.");
        return;
      }

      setStep("options");

    } catch (error) {
      
      if (data.code !== 200) {
        alert(
          error.data?.message || "Error connecting to server. Please retry.",
        );
        return;
      }

      setServerError(
        error.data?.message || "Error connecting to server. Please retry.",
      );
    } finally {
      setShowSpinner(false);
    }
  };

  const selectTypeHandler = (type) => {
    clearMessages();
    setSelectedType(type);
    resetUidFields();
    setStep("change");
  };

  const cancelSelectionHandler = () => {
    clearMessages();
    setSelectedType("");
    resetUidFields();
    setStep("options");
  };

  const submitHandler = async () => {
    clearMessages();

    if (!validateSelectedUid()) return;

    const uid = selected_uid();

    try {
      setShowSpinner(true);

      const response = await axios.post(
        "php/sign_in/change_uid.php",
        { uid: uid.trim() },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      console.log("RAW RESPONSE", response);
      console.log("DATA", response.data);
      console.log("DATA MESSAGE", response.data);

      if (response.data.code === 200) {
        alert(
          response.data.message ||
            "Your user id has been updated successfully.",
        );
        props.onCompletion();
        return;
      }

      setServerError(
        response.data?.message || "Unable to update your id. Please retry.",
      );
    } catch (error) {

      const data = error?.response?.data;
      alert(data?.message || "Error connecting to server. Please retry.");

      setServerError(
        data?.message || "Error connecting to server. Please retry.",
      );

    } finally {
      setShowSpinner(false);
    }
  };

  return (
    <div className={styles.classes}>
      {step !== "change" && (
        <>
          <div className={styles.floatRight}>
            <button
              className={styles.bgRed}
              type="button"
              onClick={props.onCancel}
            >
              Cancel
            </button>
          </div>
          <header className={styles.header}>
            <h2>Change your user id (email / phone / username)</h2>
          </header>
        </>
      )}

      {step === "password" && (
        <>
          <p>
            For additional security, please enter your current password below.
          </p>

          <div className={styles.inputView}>
            <label htmlFor="password">Current Password</label>
            <div className={styles.flex}>
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                placeholder="Enter your password"
                maxLength={100}
                required
                autoComplete="off"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setPasswordError("");
                  setServerError("");
                }}
              />
              <Checkbox
                labelClassname={styles.checkboxLabel}
                inputClassname={styles.checkbox}
                label="Show"
                checked={showPassword}
                onChange={() => setShowPassword((prev) => !prev)}
              />
            </div>
            {passwordError && (
              <span className={styles.errorText}>{passwordError}</span>
            )}
          </div>

          {serverError && <p className={styles.errorText}>{serverError}</p>}

          <button
            className={styles.bgGreen}
            type="button"
            onClick={verifyPasswordHandler}
            disabled={showSpinner}
          >
            Submit
          </button>
        </>
      )}

      {step === "options" && (
        <>
          <header>
            <h3>Select option</h3>
          </header>
          <div className={styles.btnDiv}>
            <button
              className={styles.bgGreen}
              type="button"
              onClick={() => selectTypeHandler(UID_OPTIONS.email)}
            >
              Email
            </button>
            <button
              className={styles.bgPurple}
              type="button"
              onClick={() => selectTypeHandler(UID_OPTIONS.phone)}
            >
              Phone
            </button>
            <button
              className={styles.bgBlue}
              type="button"
              onClick={() => selectTypeHandler(UID_OPTIONS.username)}
            >
              Username
            </button>
          </div>

          {serverError && <p className={styles.errorText}>{serverError}</p>}
        </>
      )}

      {step === "change" && (
        <>
          <div className={styles.floatRight}>
            <button
              className={styles.bgRed}
              type="button"
              onClick={cancelSelectionHandler}
            >
              Cancel
            </button>
          </div>

          {selectedType === UID_OPTIONS.email && (
            <>
              <header className={styles.header}>
                <h3>Change your user id - Email option</h3>
              </header>
              <p>In future, password help support may be sent to this email.</p>
              <div className={styles.inputView}>
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  placeholder="Enter your email as your new id"
                  maxLength={72}
                  required
                  autoComplete="off"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value.trim().toLowerCase());
                    setUidError("");
                    setServerError("");
                  }}
                />
              </div>
            </>
          )}

          {selectedType === UID_OPTIONS.phone && (
            <>
              <header className={styles.header}>
                <h3>Change your user id - Phone option</h3>
              </header>
              <ol className={styles.ol}>
                <li>
                  The format is + country code and your number. If your local
                  number begins with 0, omit that 0 after the country code.
                </li>
                <li>
                  In future, password help support may be sent to this phone
                  number.
                </li>
              </ol>
              <div className={styles.inputView}>
                <label htmlFor="phone">Phone Number</label>
                <input
                  id="phone"
                  placeholder="Enter your phone number as your new id"
                  maxLength={17}
                  required
                  autoComplete="off"
                  value={phone}
                  onChange={(e) => {
                    setPhone(normalizePhone(e.target.value));
                    setUidError("");
                    setServerError("");
                  }}
                />
              </div>
            </>
          )}

          {selectedType === UID_OPTIONS.username && (
            <>
              <header className={styles.header}>
                <h3>Change your user id - Username option</h3>
              </header>
              <p>
                Your username must not contain spaces and must be 8 to 15
                alphanumeric characters.
              </p>
              <div className={styles.inputView}>
                <label htmlFor="username">Username</label>
                <input
                  id="username"
                  placeholder="Enter a username as your new id"
                  maxLength={15}
                  required
                  autoComplete="off"
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value.trim().toLowerCase());
                    setUidError("");
                    setServerError("");
                  }}
                />
              </div>
            </>
          )}

          {uidError && <p className={styles.errorText}>{uidError}</p>}
          {serverError && <p className={styles.errorText}>{serverError}</p>}

          <button
            className={styles.bgGreen}
            type="button"
            onClick={submitHandler}
            disabled={showSpinner || !selectedValue.trim()}
          >
            Submit
          </button>
        </>
      )}

      {showSpinner && <Spinner />}
    </div>
  );
};

export default ChangeUid;
